import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Store } from 'antd/es/form/interface';

import { Crud, CrudMode, notification, rejectionError } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps } from '@ohpen/enso-ui';
import {
  CustomKey,
  CustomKeysToKeyMapping,
  ImportMatchResponse,
  importMatchingGetImportMatchingOfType,
  updateImportMatching,
} from '@pmp/api';

import { getFormSchema } from './formConfig';
import type { ImportMatching, ImportMatchingCrudProps } from './interfaces';

export const ImportMatchingCrud = memo(({ entityType, mode, onClose }: ImportMatchingCrudProps): ReactElement | null => {
  const ref = useRef<CrudFormComponent>(null);
  const [data, setData] = useState<ImportMatchResponse[] | null>(null);
  const [initialValues, setInitialValues] = useState<ImportMatching | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!entityType) return;
    importMatchingGetImportMatchingOfType({ path: { importEntityType: entityType } })
      .then(({ data }: { data: ImportMatchResponse[] }) => {
        const initialValues: ImportMatching = data.reduce(
          (acc: ImportMatching, { key, defaultKey, customKeys }: ImportMatchResponse) => ({
            ...acc,
            [key]: { defaultKey, customKeys },
          }),
          {} as ImportMatching,
        );
        setData(data);
        setInitialValues(initialValues);
      })
      .catch((error: unknown) => {
        console.error('Error getting import matching', error);
        notification().error({ message: t('global.errors.gettingData') });
        onClose();
      });
  }, [entityType, onClose, t]);

  const onConfirm: <T extends Store>(formData: T) => Promise<void> = useCallback(
    (values: { [key: string]: { customKeys: CustomKey[] } }) => {
      if (!entityType) return Promise.reject(rejectionError('invalid entity type', { data, values }));
      if (mode === CrudMode.EDIT) {
        if (!values) return Promise.reject(rejectionError('invalid data', { data, values }));
        const customKeysToKeyMappings: CustomKeysToKeyMapping[] = Object.entries(values).reduce(
          (arr, [key, { customKeys }]) => (key.includes('DefaultKey') ? arr : [...arr, { key, customKeys }]),
          [] as CustomKeysToKeyMapping[],
        );

        return updateImportMatching({ body: { type: entityType, customKeysToKeyMappings } }).then(() => {
          notification().success({
            message: t('global.notifications.xEditedSuccessfully', { x: t('global.labels.importHeadersMatching') }),
          });
        });
      }
      return Promise.reject(rejectionError(t('global.crud.error.noMode'), data));
    },
    [entityType, data, mode, t],
  );

  const title = useMemo(() => {
    if (!mode) return '';
    const titles: { [key: string]: string } = {
      [CrudMode.VIEW]: t('global.labels.viewX', { x: t('global.labels.importHeadersMatching') }),
      [CrudMode.EDIT]: t('global.labels.editX', { x: t('global.labels.importHeadersMatching') }),
    };

    return titles[mode];
  }, [mode, t]);

  const schema = useMemo(() => (data ? getFormSchema(entityType, data, mode) : undefined), [data, entityType, mode]);

  const crudProps: CrudProps<ImportMatching> = useMemo(() => {
    return {
      ref,
      open: true,
      schema,
      title,
      labels: { confirm: t('global.labels.save') },
      initialValues,
      mode: mode,

      onClose,
      onConfirm,
    };
  }, [onClose, onConfirm, schema, initialValues, mode, t, title]);

  if (!data) return null;

  return <Crud {...crudProps} />;
});
